'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useOrganization } from '@clerk/nextjs';
import { usePlantId } from '../lib/plantContext';
import { setSelectedPlantForOrg } from '../lib/plantContext';
import { useApiClient } from '../lib/api';
import FullScreenLoader from './FullScreenLoader';
import { useOrgProductTier } from '../lib/orgProductContext';
import { nextIncompleteLabOnboardingHref } from '../lib/limsLabOnboarding';

interface OnboardingStatus {
  hasOnboardingState: boolean;
  currentStep: string;
  completedSteps: string[];
  skippedSteps: string[];
  needsOnboarding: boolean;
  lastActiveAt?: string;
  stepData?: any;
}

interface OnboardingGuardProps {
  children: React.ReactNode;
  disabled?: boolean;
}

const STEP_ORDER = [
  'plant-basics',
  'compliance',
  'lab-ingestion',
  'invite',
  'finish'
];

const isValidPlantId = (pid?: string | null): boolean => {
  if (!pid) return false;
  return /^[A-Za-z0-9_-]{1,50}$/.test(pid);
};

// Module-level signal so step writes (from useOnboardingState.updateStep) can
// bust the OnboardingGuard's in-memory cache without prop drilling. Each call
// to invalidateOnboardingGuardCache() bumps this counter; any guard listening
// will re-run its check on its next render.
let onboardingGuardCacheVersion = 0;
const cacheVersionListeners = new Set<() => void>();
function invalidateOnboardingGuardCache() {
  onboardingGuardCacheVersion += 1;
  cacheVersionListeners.forEach(fn => {
    try { fn(); } catch {}
  });
}

export function OnboardingGuard({ children, disabled = false }: OnboardingGuardProps) {
  const { organization } = useOrganization();
  const plantId = usePlantId();
  const router = useRouter();
  const pathname = usePathname();
  const api = useApiClient();
  const { effectiveIsSlicOnly, effectiveIsLabOnly, loading: tierLoading } = useOrgProductTier();
  const blockingEnabled = process.env.NEXT_PUBLIC_ONBOARDING_BLOCKING === 'true';
  const [isChecking, setIsChecking] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const lastCheckKeyRef = useRef<string | null>(null);
  const [cacheVersion, setCacheVersion] = useState(onboardingGuardCacheVersion);

  useEffect(() => {
    const listener = () => setCacheVersion(onboardingGuardCacheVersion);
    cacheVersionListeners.add(listener);
    return () => { cacheVersionListeners.delete(listener); };
  }, []);

  // When something invalidates the cache (e.g. updateStep / skipStep wrote
  // to onboarding state), drop the in-memory key so the next render performs
  // a fresh check rather than reusing stale results.
  useEffect(() => {
    lastCheckKeyRef.current = null;
  }, [cacheVersion]);

  useEffect(() => {
    try {
      const orgId = organization?.id;
      if (orgId) {
        localStorage.setItem('selected_org_id', String(orgId));
        const maxAge = 60 * 60 * 24 * 30;
        const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
        document.cookie = `selected_org_id=${encodeURIComponent(String(orgId))}; Path=/; SameSite=Lax; Max-Age=${maxAge}${secure}`;
      }
    } catch {}
  }, [organization?.id]);

  useEffect(() => {
    if (disabled) {
      setIsChecking(false);
      return;
    }

    const path = typeof pathname === 'string' ? pathname : '';
    const isSlicRoute =
      path.startsWith('/slic') ||
      path.startsWith('/inspector') ||
      path.startsWith('/packet') ||
      path.startsWith('/verify/') ||
      path === '/' ||
      path === '/contact' ||
      path === '/privacy' ||
      path === '/terms' ||
      path === '/sms-opt-in' ||
      path.startsWith('/global-admin');

    // Never block SLIC-only console routes, public token portals, or exempt routes behind onboarding/tier loaders.
    if (isSlicRoute || effectiveIsSlicOnly) {
      setIsChecking(false);
      setShouldRedirect(false);
      setRedirectPath(null);
      return;
    }

    // Lab orgs use a separate org-scoped wizard (/onboarding/lab-*). Incomplete
    // lab onboarding must not land on /lims — send users to /onboarding/lab-profile.
    // Portal clients (lab_client) never run lab setup — they only use /lims/portal.
    if (effectiveIsLabOnly) {
      const isLabOnboarding = path.startsWith('/onboarding/lab');
      const isLims = path.startsWith('/lims');
      const isExempt =
        isLabOnboarding ||
        path.startsWith('/profile') ||
        path.startsWith('/notifications') ||
        path.startsWith('/global-admin') ||
        path.startsWith('/accept-invitation') ||
        path.startsWith('/sign-') ||
        path === '/' ||
        path === '/home';

      if (isExempt || !isLims) {
        setIsChecking(false);
        setShouldRedirect(false);
        setRedirectPath(null);
        return;
      }

      const checkKey = `lab_onboarding|${organization?.id || ''}`;
      if (lastCheckKeyRef.current === checkKey) {
        setIsChecking(false);
        return;
      }

      setIsChecking(true);
      (async () => {
        try {
          const res = await api.get('/api/lims/profile', { skipPlantParam: true, timeoutMs: 15_000 });
          const labRole = String(res?.labRole || '');
          if (labRole === 'lab_client') {
            lastCheckKeyRef.current = checkKey;
            setShouldRedirect(false);
            setRedirectPath(null);
            setIsChecking(false);
            return;
          }
          const completed = Boolean(
            res?.labOnboarding?.completed ||
            res?.labOnboarding?.completedAt ||
            res?.profile?.labOnboarding?.completedAt
          );
          lastCheckKeyRef.current = checkKey;
          if (!completed) {
            setRedirectPath(nextIncompleteLabOnboardingHref(res?.labOnboarding));
            setShouldRedirect(true);
          } else {
            setShouldRedirect(false);
            setRedirectPath(null);
          }
        } catch {
          // If profile fails, do not trap the user; let LIMS pages surface errors.
          lastCheckKeyRef.current = checkKey;
          setShouldRedirect(false);
          setRedirectPath(null);
        } finally {
          setIsChecking(false);
        }
      })();
      return;
    }

    if (tierLoading) {
      setIsChecking(true);
      return;
    }

    if (path.startsWith('/scada') || path.startsWith('/vr')) {
      setIsChecking(false);
      return;
    }

    if (!organization?.id) {
      setIsChecking(false);
      return;
    }

    const currentKey = `${organization.id}|${plantId || ''}`;
    const isOnboardingRoute = path.startsWith('/onboarding');

    // When the user is navigating WITHIN the onboarding wizard and we have already
    // completed a check for this (orgId, plantId), don't re-run the heavy
    // /api/plants + /api/onboarding/status sequence. Re-running it on every step
    // navigation produces a "Checking setup..." flash that looks like the page is
    // stuck, and on transient errors it can force the user back to plant-basics.
    if (isOnboardingRoute && lastCheckKeyRef.current === currentKey) {
      setIsChecking(false);
      setShouldRedirect(false);
      setRedirectPath(null);
      return;
    }

    checkOnboardingStatus(0);
  }, [organization?.id, plantId, disabled, pathname, cacheVersion, effectiveIsSlicOnly, effectiveIsLabOnly, tierLoading]);

  const checkOnboardingStatus = async (attempt: number) => {
    const isOnboardingRoute = typeof pathname === 'string' && pathname.startsWith('/onboarding');
    const currentKey = `${organization?.id || ''}|${plantId || ''}`;
    try {
      // Avoid flashing the full-screen "Checking setup..." loader during in-wizard
      // re-checks. The wizard pages render their own UI and we don't want the
      // entire app to disappear behind a spinner just because plantId changed
      // mid-flow (e.g. after plant-basics submitted and selected the new plant).
      // Only show the loader when:
      //  - we're NOT on an onboarding route, OR
      //  - we have no prior cached result at all (truly first render), OR
      //  - we're on a retry attempt (the previous attempt failed).
      const isFirstEverCheck = lastCheckKeyRef.current === null;
      if (!isOnboardingRoute || (isFirstEverCheck && attempt === 0) || attempt > 0) {
        setIsChecking(true);
      } else {
        setIsChecking(false);
      }
      setRetryCount(attempt);
      setError(null);

      // Sync org membership once per session
      try {
        const orgId = organization?.id;
        const key = orgId ? `org_sync_attempted:${orgId}` : 'org_sync_attempted';
        const already = typeof window !== 'undefined' ? window.sessionStorage.getItem(key) : null;
        if (!already) {
          const syncRes = await api.post('/api/org/sync', {}, { timeoutMs: 10_000 });
          try { if (typeof window !== 'undefined') window.sessionStorage.setItem(key, String(Date.now())); } catch {}
          const defaultPlantId = String(syncRes?.defaultPlantId || '').trim();
          if (defaultPlantId && orgId && !plantId) {
            setSelectedPlantForOrg(orgId, defaultPlantId);
          }
        }
      } catch {}

      const plantsResponse = await api.get('/api/plants', { timeoutMs: 15_000, dedupe: false });

      if (!plantsResponse?.success) {
        if (isOnboardingRoute) {
          lastCheckKeyRef.current = currentKey;
          setIsChecking(false);
          return;
        }
        setRedirectPath('/onboarding/plant-basics');
        setShouldRedirect(true);
        setIsChecking(false);
        return;
      }

      const plants = plantsResponse?.plants || [];
      const totalCount = plantsResponse?.totalCount || 0;
      const realPlants = plants.filter((p: any) => p.plantId && p.status !== 'inactive');
      const hasRealPlants = realPlants.length > 0 || (totalCount > plants.length && plants.length > 0);

      if (!hasRealPlants && totalCount === 0) {
        if (isOnboardingRoute) {
          lastCheckKeyRef.current = currentKey;
          setIsChecking(false);
          return;
        }
        setRedirectPath('/onboarding/plant-basics');
        setShouldRedirect(true);
        setIsChecking(false);
        return;
      }

      if (realPlants.length === 0 && totalCount > plants.length) {
        lastCheckKeyRef.current = currentKey;
        setIsChecking(false);
        return;
      }

      // Auto-select the first plant if none selected
      if (!plantId) {
        if (realPlants.length > 0) {
          const orgId = organization?.id;
          if (orgId) {
            setSelectedPlantForOrg(orgId, realPlants[0].plantId);
          }
          // Don't cache here — the plantId is about to change, which will
          // re-trigger this effect and produce a new cache key.
          setIsChecking(false);
          return;
        } else {
          if (isOnboardingRoute) {
            lastCheckKeyRef.current = currentKey;
            setIsChecking(false);
            return;
          }
          setRedirectPath('/onboarding/plant-basics');
          setShouldRedirect(true);
          setIsChecking(false);
          return;
        }
      }

      // Non-blocking mode: allow app to load when plants exist
      if (!blockingEnabled) {
        lastCheckKeyRef.current = currentKey;
        setIsChecking(false);
        return;
      }

      if (!isValidPlantId(plantId)) {
        if (isOnboardingRoute) {
          lastCheckKeyRef.current = currentKey;
          setIsChecking(false);
          return;
        }
        setRedirectPath('/onboarding/plant-basics');
        setShouldRedirect(true);
        setIsChecking(false);
        return;
      }

      const status: OnboardingStatus = await api.get(`/api/onboarding/status?plantId=${plantId}`);

      if (status.needsOnboarding) {
        const nextStep = determineNextStep(status);
        if (isOnboardingRoute) {
          lastCheckKeyRef.current = currentKey;
          setIsChecking(false);
          return;
        }
        setRedirectPath(`/onboarding/${nextStep}`);
        setShouldRedirect(true);
      }

      lastCheckKeyRef.current = currentKey;
      setIsChecking(false);
    } catch (err) {
      console.error('Error checking onboarding status:', err);
      setError(err instanceof Error ? err.message : 'Unable to check setup status');
      const nextAttempt = attempt + 1;
      setRetryCount(nextAttempt);

      if (attempt < 3) {
        setTimeout(() => {
          checkOnboardingStatus(nextAttempt);
        }, Math.pow(2, attempt) * 1000);
        return;
      }

      // If the user is already inside the onboarding wizard, do not yank them
      // back to /onboarding/plant-basics on a transient status check failure —
      // that's how a user mid-wizard ends up "always reloading plant-basics"
      // when the network briefly hiccups. Let the wizard page render with its
      // own (possibly empty) state and decide what to do.
      if (!isOnboardingRoute) {
        setRedirectPath('/onboarding/plant-basics');
        setShouldRedirect(true);
      }
      setIsChecking(false);
    }
  };

  const determineNextStep = (status: OnboardingStatus): string => {
    if (!status.hasOnboardingState) {
      return 'plant-basics';
    }

    for (const step of STEP_ORDER) {
      if (!status.completedSteps.includes(step) && !status.skippedSteps.includes(step)) {
        return step;
      }
    }

    return 'finish';
  };

  useEffect(() => {
    if (shouldRedirect && redirectPath) {
      const timer = setTimeout(() => {
        router.push(redirectPath);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [shouldRedirect, redirectPath, router]);

  if (error && retryCount >= 3) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Setup Check Failed</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500 mb-6">You can try again or continue to the dashboard.</p>
          <div className="space-y-3">
            <button
              onClick={() => {
                setError(null);
                setRetryCount(0);
                checkOnboardingStatus(0);
              }}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => setIsChecking(false)}
              className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
            >
              Continue to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isChecking) {
    return (
      <FullScreenLoader
        message={retryCount > 0 ? `Retrying... (${retryCount}/3)` : 'Checking setup...'}
      />
    );
  }

  if (shouldRedirect) {
    return <FullScreenLoader message="Setting up your facility..." />;
  }

  return <>{children}</>;
}

/**
 * Hook to check if onboarding is required
 */
export function useOnboardingRequired() {
  const { organization } = useOrganization();
  const plantId = usePlantId();
  const api = useApiClient();
  const [isRequired, setIsRequired] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!organization?.id || plantId === 'default' || !isValidPlantId(plantId)) {
      setIsRequired(false);
      setIsLoading(false);
      return;
    }

    checkRequirement();
  }, [organization?.id, plantId]);

  const checkRequirement = async () => {
    try {
      const status: OnboardingStatus = await api.get(`/api/onboarding/status?plantId=${plantId}`);
      setIsRequired(status.needsOnboarding);
    } catch (error) {
      console.error('Error checking onboarding requirement:', error);
      setIsRequired(false);
    } finally {
      setIsLoading(false);
    }
  };

  return { isRequired, isLoading };
}

/**
 * Hook to manage onboarding state
 */
export function useOnboardingState() {
  const { organization } = useOrganization();
  const plantId = usePlantId();
  const api = useApiClient();
  const [state, setState] = useState<OnboardingStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const getCachedPlantId = (): string | null => {
    try {
      const orgId = organization?.id;
      if (!orgId) return null;
      return localStorage.getItem(`selectedPlant_${orgId}`);
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (!organization?.id || plantId === 'default' || !isValidPlantId(plantId)) {
      setState(null);
      setIsLoading(false);
      return;
    }

    loadState();
  }, [organization?.id, plantId]);

  const loadState = async (plantIdOverride?: string) => {
    try {
      const targetPlantId = isValidPlantId(plantIdOverride)
        ? plantIdOverride!
        : (isValidPlantId(plantId) ? plantId : (getCachedPlantId() || ''));
      if (!isValidPlantId(targetPlantId)) {
        setState(null);
        return;
      }
      const status: OnboardingStatus = await api.get(`/api/onboarding/status?plantId=${targetPlantId}`);
      setState(status);
    } catch (error) {
      console.error('Error loading onboarding state:', error);
      setState(null);
    } finally {
      setIsLoading(false);
    }
  };

  const updateStep = async (
    currentStep: string,
    stepData?: any,
    completed?: boolean,
    plantIdOverride?: string,
  ) => {
    const effectivePlantId = isValidPlantId(plantIdOverride)
      ? plantIdOverride!
      : (isValidPlantId(plantId) ? plantId : (getCachedPlantId() || ''));
    if (!isValidPlantId(effectivePlantId)) {
      throw new Error('Cannot update onboarding step: invalid or missing plantId');
    }
    await api.post('/api/onboarding/step', {
      plantId: effectivePlantId,
      currentStep,
      stepData,
      completed
    });
    invalidateOnboardingGuardCache();
    await loadState(effectivePlantId);
  };

  const skipStep = async (step: string, plantIdOverride?: string) => {
    const effectivePlantId = isValidPlantId(plantIdOverride)
      ? plantIdOverride!
      : (isValidPlantId(plantId) ? plantId : (getCachedPlantId() || ''));
    if (!isValidPlantId(effectivePlantId)) {
      throw new Error('Cannot skip onboarding step: invalid or missing plantId');
    }
    await api.post('/api/onboarding/step', {
      plantId: effectivePlantId,
      currentStep: step,
      skipped: true
    });
    invalidateOnboardingGuardCache();
    await loadState(effectivePlantId);
  };

  return {
    state,
    isLoading,
    updateStep,
    skipStep,
    refresh: loadState
  };
}
